<template>
  <div class="container" :class="{ fullscreen: presentFullscreen }">
    <div class="top-container">
      <span>{{ title }}</span>
      <template v-if="additionalInfo">
        <button class="info-button" @click="toggleInfo">
          <GraphIcon v-if="presentInformation" class="icon" />
          <InfoIcon v-else class="icon" />
        </button>
      </template>
      <button class="fullscreen-button" @click="toggleFullscreen">
        <FullScreenOpenIcon v-if="!presentFullscreen" class="icon" />
        <FullScreenCloseIcon v-else class="icon" />
      </button>
    </div>
    <template v-if="presentInformation">
      <template v-if="additionalInfo">
        <div class="information-container">
          <MarkDown :source="additionalInfo" />
        </div>
      </template>
    </template>
    <template v-else>
      <div :key="componentKey" class="content-container">
        <template v-if="state === 'LOADING'">
          <slot name="loading">
            <PulseLoader :loading="true" color="#3F46D6" />
          </slot>
        </template>
        <template v-else-if="hasError">
          <slot name="error">
            <span>{{ hasError.message }}</span>
          </slot>
        </template>
        <template v-else-if="state === 'EMPTY' || state === 'DISABLED'">
          <slot name="empty">
            <span class="empty-text">{{ filterMessage }}</span>
          </slot>
        </template>
        <template v-else>
          <slot> </slot>
        </template>
      </div>
    </template>
    <div class="card-footer">
      <slot name="footer"> </slot>
    </div>
  </div>
  <div v-if="presentFullscreen" class="overlay" @click="toggleFullscreen"></div>
</template>
<script lang="ts" setup>
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import GraphIcon from '@/design-system/assets/icons/IconTimeLine.vue'
import InfoIcon from '@/design-system/assets/icons/IconInfoCircle.vue'
import FullScreenOpenIcon from '@/design-system/assets/icons/IconMaximize2.vue'
import FullScreenCloseIcon from '@/design-system/assets/icons/IconMinimize2.vue'
import { ref } from 'vue'
import { CardState } from '@/components/types'
import MarkDown from '@/components/MarkDown.vue'

type Props = {
  title: string
  hasError?: Error
  state: CardState
  filterMessage: string
  additionalInfo?: string
}

type Emits = {
  (e: 'isPresentingInfo', value: boolean): void
}

const emit = defineEmits<Emits>()

const toggleInfo = () => {
  presentInformation.value = !presentInformation.value
  emit('isPresentingInfo', presentInformation.value)
}
const toggleFullscreen = () => {
  presentFullscreen.value = !presentFullscreen.value
  componentKey++
}
const presentInformation = ref(false)
const presentFullscreen = ref(false)
let componentKey = 0
defineProps<Props>()
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/mixins.scss';
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';

.overlay {
  background: rgba(0, 0, 0, 0.5);
  height: 100%;
  left: 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 999;
}
.empty-text {
  @include font-mixins.desktop-regular-paragraph;
}
.container {
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 8px;
  border-radius: 8px;
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.25);
  height: 450px;
  background-color: variables.$color-pure-white;

  &.fullscreen {
    position: fixed;
    top: calc(#{variables.$layout-top-bar-height} + 16px);
    left: 16px;
    right: 16px;
    bottom: 16px;
    height: auto;
    background-color: white;
    z-index: 1000;
  }
  .top-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    span {
      display: flex;
      flex-grow: 1;
    }
    button {
      all: unset;
      display: flex;
      justify-content: center;
      align-items: center;
      flex-shrink: 0;
      border-radius: 4px;
      padding: 4px;
      cursor: pointer;
      .icon {
        color: variables.$color-greyscale-secondary;
        width: 16px;
        height: 16px;
      }
      &:focus-visible {
        outline: auto;
      }
    }
    @include font-mixins.desktop-bold-label;
    color: variables.$color-text-primary;
    position: relative;
    left: 0;
    top: 0;
    right: 0;
  }
  .content-container {
    display: flex;
    flex-grow: 1;
    align-items: center;
    justify-content: center;
    height: 300px;
  }
  .information-container {
    display: flex;
    overflow-y: auto;
    @include mixins.scrollbar-vertical;

    p {
      @include font-mixins.desktop-regular-label;
      padding-right: 16px;
      height: max-content;
    }
    .strong {
      font-size: 30px;
    }
  }
  .card-footer {
    flex-shrink: 0;
  }
}
</style>
